import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import Label from "../../../../../shared/ui/form/Label";
import Input from "../../../../../shared/ui/form/input/InputField";
import Button from "../../../../../shared/ui/button/Button";
import { GroupIcon, InfoIcon } from "../../../../../icons";
import { useCenter } from "../../../../center";
import { MIN_GROUP_SIZE } from "../../../domain/GroupPatient";
import { groupErrorMessage, useCreateGroupPatient } from "../../../application/useGroupPatients";
import { usePatientOptions } from "../../../application/usePatientOptions";
import GroupMemberPicker from "../GroupMemberPicker";
import { PatientOwnershipChoice } from "../PatientOwnership";

interface Props {
    therapistID: string;
}

/**
 * Taking on a group: three or more of the therapist's own patients.
 *
 * Inside a centre the group is the centre's or the therapist's, and every
 * member has to match — only the people that fit the answer are offered,
 * the same as for a couple.
 */
export default function GroupPatientAddForm({ therapistID }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { center } = useCenter();
    const { options, loading } = usePatientOptions(therapistID);
    const create = useCreateGroupPatient();

    const [heldByCenter, setHeldByCenter] = useState(true);
    const [memberIds, setMemberIds] = useState<string[]>([]);
    const [displayName, setDisplayName] = useState("");
    const [error, setError] = useState<string | null>(null);

    const offered = center ? options.filter((o) => o.heldByCenter === heldByCenter) : options;
    const byId = new Map(options.map((option) => [option.value, option]));
    const members = memberIds.map((id) => ({ id, name: byId.get(id)?.label ?? "" }));
    const candidates = offered.filter((option) => !memberIds.includes(option.value));

    const chooseOwnership = (value: boolean) => {
        setHeldByCenter(value);
        // Members picked under the other answer are no longer on offer.
        setMemberIds([]);
    };

    const handleSubmit = async () => {
        setError(null);
        if (memberIds.length < MIN_GROUP_SIZE) {
            setError(t("patient.group.tooFew", { min: MIN_GROUP_SIZE }));
            return;
        }
        try {
            const group = await create.mutateAsync({
                patient_ids: memberIds,
                display_name: displayName.trim() || undefined,
            });
            navigate(`/group-patient/${group.id}`);
        } catch (err) {
            setError(groupErrorMessage(err, t));
        }
    };

    return (
        <ComponentCard
            glass={true}
            title={t("patient.group.newTitle")}
            className="overflow-hidden shadow-xl border-white/20 dark:border-white/10"
        >
            <div className="space-y-6">
                {center && (
                    <PatientOwnershipChoice
                        centerName={center.name}
                        heldByCenter={heldByCenter}
                        onChange={chooseOwnership}
                        name="group-ownership"
                        question={t("patient.group.question")}
                    />
                )}

                <GroupMemberPicker
                    members={members}
                    candidates={candidates}
                    loading={loading}
                    keepMinimum={false}
                    onAdd={(id) => setMemberIds((current) => [...current, id])}
                    onRemove={(member) =>
                        setMemberIds((current) => current.filter((id) => id !== member.id))
                    }
                />

                <div>
                    <Label htmlFor="group-name">
                        {t("patient.group.name")} ({t("common.optional")})
                    </Label>
                    <Input
                        id="group-name"
                        type="text"
                        startIcon={<GroupIcon className="size-5" />}
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder={t("patient.group.namePlaceholder")}
                        hint={t("patient.group.nameHint")}
                    />
                </div>

                <div className="flex gap-3 rounded-xl border border-brand-100 bg-brand-50/50 p-4 dark:border-brand-800/50 dark:bg-brand-900/10">
                    <InfoIcon className="mt-0.5 size-5 shrink-0 text-brand-600 dark:text-brand-400" />
                    <div>
                        <h4 className="mb-1 text-sm font-semibold text-brand-900 dark:text-brand-100">
                            {t("patient.group.noticeTitle")}
                        </h4>
                        <p className="text-xs leading-relaxed text-brand-700/80 dark:text-brand-400/80">
                            {t("patient.group.noticeDesc")}
                        </p>
                    </div>
                </div>

                {error && (
                    <p role="alert" className="text-center text-sm font-medium text-red-500 dark:text-red-400">
                        {error}
                    </p>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                    <Button variant="outline" className="px-8" size="md" onClick={() => navigate("/patients?tab=group")}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        loading={create.isPending}
                        disabled={create.isPending}
                        className="min-w-[120px] px-8"
                        size="md"
                    >
                        {t("common.save")}
                    </Button>
                </div>
            </div>
        </ComponentCard>
    );
}
